/**
 * D-EZ-109 — THE SPONSOR DESK. Only what the books hold.
 *
 *   • the winner's primary sponsor: its first win with him, or its count, and
 *     how many seasons it has been on the car (`sponsorSlotsFor`, `seasonsHeld`);
 *   • new names signed this week — major, associate, the one-race special —
 *     from the season's own sponsor log (`SponsorChange`), or, for a settled
 *     season whose log the archive dropped, from contracts signed at race N;
 *   • contract notes the log holds: a month of primary money lost to a driver's
 *     remarks, a warning on the record (SAG/16-17);
 *   • a long-tenured sponsor on a car that ran up front this week, once a
 *     season per car.
 *
 * ⚠ NEVER A SIGNING OR A FIRING THE BOOKS DO NOT SHOW. A quiet week on the
 * desk prints nothing.
 */

import { sponsorSlotsFor } from '../finance/sponsorNames.ts';
import { num, say, type WireLine } from './line.ts';
import type { WeekContext } from './week.ts';

const T = {
  firstWin: [
    'First win for {sponsor} with {driver} — in their first season together.',
    '{sponsor} gets its first trip to victory lane with {driver}.',
  ],
  firstWinLong: [
    'It took {seasons} seasons, but {sponsor} finally has a win with {driver}.',
    'First win for {sponsor} on {driver}’s car, {seasons} seasons into the deal.',
  ],
  nthWin: [
    'Win number {n} for {sponsor} with {driver}.',
    '{sponsor} has now been to victory lane {n} times with {driver}.',
    'That is {n} wins with {driver} for {sponsor}.',
  ],
  signed: [
    '{driver} adds {sponsor} as a new {tier} sponsor, worth {amount} a year.',
    'New money: {sponsor} signs on with {driver} as a {tier} sponsor ({amount} a year).',
    '{sponsor} comes aboard {driver}’s car as a {tier} sponsor — {amount} a year.',
  ],
  signedNoAmount: ['{driver} adds {sponsor} as a new {tier} sponsor.', '{sponsor} signs on with {driver} as a {tier} sponsor.'],
  special: [
    '{sponsor} rode on {driver}’s car this weekend only.',
    'One-week deal: {sponsor} took the special slot on {driver}’s car.',
  ],
  blackout: [
    '{driver}’s remarks about {sponsor} cost his team a month of primary money.',
    '{sponsor} has pulled a month of money from {driver}’s team after his remarks.',
  ],
  warned: ['{sponsor} warned {driver} about his remarks; it is on the record now.', '{driver} keeps {sponsor}, with a warning about his mouth.'],
  tenure: [
    '{sponsor} is in its {seasons} season on {driver}’s car.',
    '{seasons} season together for {driver} and {sponsor}.',
  ],
} as const;

const TIER_WORD: Record<string, string> = { major: 'major', associate: 'associate', primary: 'primary' };

/** Wins `driverId` has had in seasons `from`…S, through race N. */
function winsSince(w: WeekContext, driverId: string, fromSeason: number, fromRace: number): number {
  let n = 0;
  for (const s of w.seasons) {
    if (s.seasonNumber < fromSeason) continue;
    for (const r of s.season.races) {
      if (s.seasonNumber === fromSeason && r.raceNumber < fromRace) continue;
      if (r.race.winnerId === driverId) n++;
    }
  }
  return n;
}

export function sponsorDesk(w: WeekContext, max = 4): WireLine[] {
  const out: WireLine[] = [];
  const key = (id: string) => `${w.seasonKey}|${id}`;

  // ---- the winner's sponsor ------------------------------------------------------
  const winnerId = w.run.race.winnerId;
  const dfs = winnerId ? w.contracts[winnerId] : undefined;
  const primary = dfs ? sponsorSlotsFor(dfs).find((s) => s.slot === 'Primary') : undefined;
  if (winnerId && primary && primary.name !== 'Unnamed sponsor') {
    const fromSeason = w.seasonNumber - primary.seasons + 1;
    const wins = winsSince(w, winnerId, fromSeason, primary.signedRace);
    const slots = { sponsor: primary.name, driver: w.short(winnerId), seasons: num(primary.seasons, 'word'), n: num(wins, 'word') };
    const line =
      wins === 1
        ? say(primary.seasons > 1 ? 'sponsor-first-win-long' : 'sponsor-first-win', primary.seasons > 1 ? T.firstWinLong : T.firstWin, slots, { key: key(winnerId), driverIds: [winnerId] })
        : wins > 1
          ? say('sponsor-nth-win', T.nthWin, slots, { key: key(winnerId), occurrence: wins, driverIds: [winnerId] })
          : null;
    if (line) out.push(line);
  }

  // ---- this week's signings ---------------------------------------------------------
  const rank = (id: string) => w.table.byDriver[id]?.rank ?? 999;
  type Signing = { driverId: string; tier: string; sponsor: string; yearlyMb: number | null; special: boolean };
  const signings: Signing[] = [];
  if (w.sponsorChangesHeld) {
    for (const c of w.sponsorChanges) {
      if (!c.sponsorName) continue;
      if (c.kind === 'signed-major' || c.kind === 'signed-associate') {
        const tier = c.kind === 'signed-major' ? 'major' : 'associate';
        const contract = [...(w.contracts[c.driverId]?.[tier] ?? [])].find((x) => x.sponsorName === c.sponsorName && x.signedRace === w.raceNumber);
        signings.push({ driverId: c.driverId, tier, sponsor: c.sponsorName, yearlyMb: contract?.yearlyMb ?? null, special: false });
      } else if (c.kind === 'signed-one-race-associate') {
        signings.push({ driverId: c.driverId, tier: 'associate', sponsor: c.sponsorName, yearlyMb: null, special: true });
      }
    }
  } else {
    for (const [id, d] of Object.entries(w.contracts)) {
      for (const tier of ['major', 'associate'] as const) {
        for (const c of d[tier]) {
          if (c.signedRace === w.raceNumber && c.sponsorName) signings.push({ driverId: id, tier, sponsor: c.sponsorName, yearlyMb: c.yearlyMb, special: false });
        }
      }
    }
  }
  signings.sort((a, b) => Number(a.special) - Number(b.special) || (b.tier === 'major' ? 1 : 0) - (a.tier === 'major' ? 1 : 0) || rank(a.driverId) - rank(b.driverId));
  let specials = 0;
  let deals = 0;
  for (const s of signings) {
    if (out.length >= max - 1) break;
    if (s.special) {
      if (specials++ >= 1) continue;
      const l = say('sponsor-special', T.special, { sponsor: s.sponsor, driver: w.short(s.driverId) }, { key: key(s.driverId), driverIds: [s.driverId] });
      if (l) out.push(l);
      continue;
    }
    if (deals++ >= 2) continue;
    const slots = { sponsor: s.sponsor, driver: w.short(s.driverId), tier: TIER_WORD[s.tier], amount: s.yearlyMb !== null ? num(s.yearlyMb, 'money') : null };
    const l = say(s.yearlyMb !== null ? 'sponsor-signed' : 'sponsor-signed-plain', s.yearlyMb !== null ? T.signed : T.signedNoAmount, slots, { key: key(s.driverId), driverIds: [s.driverId] });
    if (l) out.push(l);
  }

  // ---- contract notes the log holds -----------------------------------------------------
  for (const c of w.sponsorChanges) {
    if (out.length >= max) break;
    if (c.kind !== 'primary-blackout' && c.kind !== 'primary-warned') continue;
    const sponsor = w.contracts[c.driverId]?.primary?.sponsorName;
    if (!sponsor) continue;
    const l = say(c.kind === 'primary-blackout' ? 'sponsor-blackout' : 'sponsor-warned', c.kind === 'primary-blackout' ? T.blackout : T.warned, { sponsor, driver: w.short(c.driverId) }, { key: key(c.driverId), driverIds: [c.driverId] });
    if (l) out.push(l);
  }

  // ---- a long marriage, on a car that ran up front: once a season per car -----------
  if (out.length < max) {
    for (const f of w.run.race.finishingOrder.slice(0, 5)) {
      if (f.driverId === winnerId) continue;
      const p = w.contracts[f.driverId]?.primary;
      if (!p?.sponsorName || (p.seasonsHeld ?? 1) < 3) continue;
      const earlier = w.races.some((r) => r.raceNumber < w.raceNumber && r.race.finishingOrder.some((x) => x.driverId === f.driverId && x.place <= 5));
      if (earlier) continue;
      const l = say('sponsor-tenure', T.tenure, { sponsor: p.sponsorName, driver: w.short(f.driverId), seasons: num(p.seasonsHeld ?? 1, 'ord') }, { key: key(f.driverId), driverIds: [f.driverId] });
      if (l) {
        out.push(l);
        break;
      }
    }
  }
  return out.slice(0, max);
}
